import {USER_PROFILE_ALLOCATIONS} from '@/constants/engine';
import {ModelFactory, ModelCondition, DependencyRegistrer, mapValueDomainList} from '@/engine';
import {getDefaultMaskByFieldType} from '@/engine/constants/mask';
import {ValueType as EngineValueType} from '@/engine/types';
import {
  Reference,
  InputField,
  SupportsSwitch,
  DomainValueReference,
  LocalizedString,
  TemplateValue,
  Field,
  ValueDefinition,
  GeneratedValue,
  GeneratedList,
  Option,
  ResolvableValue,
  UnwrapSwitch,
} from '@/model/types';
import {getValueDependencies} from '../utils/dependencies';
import {createConditionFactory} from './condition';
import {createFactoryFromGeneratedList} from './generated';
import {createFactoryFromPlugin, registerPluginFilters} from './plugin';
import {createFactoryFromSwitch, SwitchTransform} from './switch';

const isGeneratedList = (value: unknown): value is GeneratedList<ResolvableValue | Option> => {
  if (!value || typeof value !== 'object' || !('$generated' in value)) {
    return false;
  }
  const generated = value.$generated;
  return Boolean(
    generated && typeof generated === 'object' && ['$list', '$repeat', '$each'].some(key => key in generated),
  );
};

export const computeTitle = (
  title?: SupportsSwitch<TemplateValue | LocalizedString>,
  dependencyRegistrer?: DependencyRegistrer,
): ModelFactory<TemplateValue | LocalizedString> => {
  if (!title) return () => ({fr: '', en: ''});

  if ('$switch' in title) {
    return createFactoryFromSwitch(title, {fr: '', en: ''}, {dependencyRegistrer});
  }

  return () => title;
};

type InitialValueType = EngineValueType | EngineValueType[] | undefined;
export const computeInitialValue = (
  initialValue: InputField['initialValue'],
  dependencyRegistrer?: DependencyRegistrer,
): ModelFactory<InitialValueType> => {
  if (!initialValue) {
    return () => '';
  }

  if (typeof initialValue !== 'string' && '$switch' in initialValue) {
    return createFactoryFromSwitch(initialValue, undefined, {
      dependencyRegistrer,
      transform: (value, valueResolver) => {
        if (isGeneratedList(value)) {
          const factory = createFactoryFromGeneratedList(value, {dependencyRegistrer});
          return factory(valueResolver, new Set());
        }
        return value;
      },
    });
  }

  if (isGeneratedList(initialValue)) {
    return createFactoryFromGeneratedList(initialValue, {dependencyRegistrer});
  }

  const nonGeneratedListValue = initialValue;
  dependencyRegistrer?.('all', ...getValueDependencies(nonGeneratedListValue));
  return () => nonGeneratedListValue;
};

type ValueType = string | DomainValueReference | TemplateValue | GeneratedValue | undefined;
export const computeValue = (
  value: InputField['value'],
  dependencyRegistrer?: DependencyRegistrer,
): ModelFactory<ValueType> => {
  if (!value) return () => undefined;

  const process = (value: ValueType) => {
    if (value != undefined) dependencyRegistrer?.('all', ...getValueDependencies(value));
    return value;
  };

  if (typeof value !== 'string' && '$switch' in value) {
    return createFactoryFromSwitch(value, undefined, {process: process, dependencyRegistrer});
  }

  dependencyRegistrer?.('all', ...getValueDependencies(value));

  return () => value;
};

export const computeReadonlyIf = (
  readonlyIf?: InputField['readonlyIf'],
  dependencyRegistrer?: DependencyRegistrer,
): ModelCondition[] => {
  if (Array.isArray(readonlyIf)) {
    return readonlyIf.map(condition => createConditionFactory(condition, dependencyRegistrer));
  }

  return [() => () => !!readonlyIf];
};

export const computeRequiredIf = (
  requiredIf?: InputField['requiredIf'],
  dependencyRegistrer?: DependencyRegistrer,
): ModelCondition[] => {
  if (Array.isArray(requiredIf)) {
    return requiredIf.map(condition => createConditionFactory(condition, dependencyRegistrer));
  }

  return [() => () => !!requiredIf];
};

export const computeDisabledIf = (
  disabledIf?: InputField['disabledIf'],
  dependencyRegistrer?: DependencyRegistrer,
): ModelCondition[] => {
  if (Array.isArray(disabledIf)) {
    return disabledIf.map(condition => createConditionFactory(condition, dependencyRegistrer));
  }

  return [() => () => !!disabledIf];
};

export const computeMask = (
  type: string,
  mask?: SupportsSwitch<string>,
  dependencyRegistrer?: DependencyRegistrer,
): ModelFactory<string | undefined> => {
  if (!mask) {
    const defaultMask = getDefaultMaskByFieldType(type);
    return () => defaultMask;
  }

  if (typeof mask !== 'object' || !('$switch' in mask)) return () => mask;

  return createFactoryFromSwitch(mask, undefined, {dependencyRegistrer});
};

export const computeMaxLength = (
  maxLength: Extract<Field, {type: 'text'}>['maxLength'],
  dependencyRegistrer?: DependencyRegistrer,
): ModelFactory<number | undefined> => {
  if (maxLength === undefined) return () => undefined;

  if (typeof maxLength === 'number') return () => maxLength;

  if (typeof maxLength === 'object' && '$switch' in maxLength) {
    return createFactoryFromSwitch(maxLength, undefined, {dependencyRegistrer});
  }

  dependencyRegistrer?.('all', ...getValueDependencies(maxLength));

  return valueResolver => valueResolver(maxLength);
};

type Options = Extract<Field, {type: 'select'}>['options'];

export const computeOptions = (
  options: Options | undefined,
  values: ValueDefinition[],
  dependencyRegistrer?: DependencyRegistrer,
): ModelFactory<(string | LocalizedString | DomainValueReference | Option)[]> => {
  if (!options) return () => [];

  // Handle switch case first
  if ('$switch' in options) {
    const processInner = (innerOptions: UnwrapSwitch<Options>) => {
      if ('$plugin' in innerOptions) {
        if (innerOptions.filters) {
          registerPluginFilters(innerOptions, {dependencyRegistrer});
        }
        return innerOptions;
      }

      if ('$generated' in innerOptions) {
        return innerOptions;
      }

      registerFiltersDependencies(innerOptions, dependencyRegistrer);
      return innerOptions;
    };

    const transformInner: SwitchTransform<UnwrapSwitch<Options>, (DomainValueReference | Option)[]> = (
      innerOptions,
      valueResolver,
      _store,
    ) => {
      if ('$plugin' in innerOptions) {
        const factory = createFactoryFromPlugin(innerOptions, {dependencyRegistrer});
        return factory(valueResolver, new Set()) as (DomainValueReference | Option)[];
      }

      if ('$generated' in innerOptions) {
        const factory = createFactoryFromGeneratedList(innerOptions, {dependencyRegistrer});
        return factory(valueResolver, new Set()) as (DomainValueReference | Option)[];
      }
      return mapValueDomainList(values, innerOptions, valueResolver);
    };

    return createFactoryFromSwitch(options, [], {
      process: processInner,
      transform: transformInner,
      dependencyRegistrer,
    });
  }

  if ('$plugin' in options) {
    return createFactoryFromPlugin(options, {dependencyRegistrer});
  }

  if ('$generated' in options) {
    return createFactoryFromGeneratedList(options, {dependencyRegistrer});
  }

  const process = (options: Reference) => {
    registerFiltersDependencies(options, dependencyRegistrer);
    return options;
  };

  const transform: SwitchTransform<Reference, DomainValueReference[] | Option[]> = (options, valueResolver) =>
    mapValueDomainList(values, options, valueResolver);

  if (options.$ref === USER_PROFILE_ALLOCATIONS) {
    return valueResolver => {
      const allocationOptions = valueResolver(
        {$ref: `_${USER_PROFILE_ALLOCATIONS}[options]`},
        {keepDomainReference: true},
      );
      if (!allocationOptions) return [];
      const allocationRefs = JSON.parse(allocationOptions);
      return allocationRefs.map((allocationRef: string) => {
        return {$ref: allocationRef};
      });
    };
  }

  process(options);

  return valueResolver => transform(options, valueResolver);
};

const registerFiltersDependencies = (options: Reference, dependencyRegistrer?: DependencyRegistrer) => {
  const dependencies: string[] = [];
  if ('filters' in options && Array.isArray(options.filters)) {
    for (const filter of options.filters) {
      if ('field' in filter) {
        if (typeof filter.field === 'object' && '$ref' in filter.field) {
          dependencies.push(filter.field.$ref);
        }
      } else {
        if (Array.isArray(filter.value)) {
          dependencies.push(...filter.value);
        } else if (typeof filter.value === 'object' && '$ref' in filter.value) {
          dependencies.push(filter.value.$ref);
        }
      }
    }
    dependencyRegistrer?.('all', ...dependencies);
  }
};

export const computeLabel = (
  label: SupportsSwitch<TemplateValue | LocalizedString | undefined>,
  dependencyRegistrer?: DependencyRegistrer,
): ModelFactory<TemplateValue | LocalizedString> => {
  if (!label) return () => ({fr: '', en: ''});

  if ('$switch' in label) {
    return createFactoryFromSwitch(label, {fr: '', en: ''}, {dependencyRegistrer});
  }

  return () => label;
};

export const computeHelper = (
  helper: SupportsSwitch<TemplateValue | LocalizedString | undefined>,
  dependencyRegistrer?: DependencyRegistrer,
): ModelFactory<TemplateValue | LocalizedString | undefined> => {
  if (!helper) return () => undefined;

  if ('$switch' in helper) {
    return createFactoryFromSwitch(helper, {fr: '', en: ''}, {dependencyRegistrer});
  }

  return () => helper;
};
